/**
 * AdminProfile — ADR-0022 admin scope.
 *
 * Strona /profile dla zalogowanego admina (kazda rola). Pozwala na:
 *  1. Edycja `name` (PATCH /admin/me).
 *  2. Zmiana danych logowania — email i/lub username (POST /admin/me/change-identifier)
 *     z challenge'em currentPassword. Po sukcesie all sessions revoked → user wylogowany.
 *  3. Zmiana hasla (POST /admin/me/change-password) z currentPassword + newPassword.
 *     Po sukcesie all sessions revoked → user wylogowany.
 *
 * Brak self-edit role/isActive — to robi sie przez panel adminow (krok 13) tylko
 * dla innych userów; własnej roli nie wolno zmieniać (last super-admin guard tam).
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isValidUsername } from '@saas/shared';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface BackendError {
  error?: string;
  details?: string[] | Record<string, string[]>;
}

function policyErrorsToMessages(details: string[] | undefined, t: (k: string) => string): string[] {
  if (!details) return [];
  return details.map((code) => {
    switch (code) {
      case 'TOO_SHORT':
        return t('auth.adminReset.policy.tooShort');
      case 'TOO_LONG':
        return t('auth.adminReset.policy.tooLong');
      case 'NO_DIGIT':
        return t('auth.adminReset.policy.noDigit');
      case 'NO_UPPER':
        return t('auth.adminReset.policy.noUpper');
      case 'NO_LOWER':
        return t('auth.adminReset.policy.noLower');
      case 'NO_SPECIAL':
        return t('auth.adminReset.policy.noSpecial');
      default:
        return code;
    }
  });
}

export function AdminProfile() {
  const { t } = useTranslation();
  const { admin, refresh, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // Profile (name) section.
  const [name, setName] = useState(admin?.name ?? '');
  const [savingName, setSavingName] = useState(false);

  // Identifier section.
  const [newEmail, setNewEmail] = useState(admin?.email ?? '');
  const [newUsername, setNewUsername] = useState(admin?.username ?? '');
  const [identifierPassword, setIdentifierPassword] = useState('');
  const [savingIdentifier, setSavingIdentifier] = useState(false);
  const [identifierFieldErrors, setIdentifierFieldErrors] = useState<Record<string, string>>({});
  const [identifierApiError, setIdentifierApiError] = useState<string | null>(null);

  // Password section.
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordPolicyErrors, setPasswordPolicyErrors] = useState<string[]>([]);
  const [passwordApiError, setPasswordApiError] = useState<string | null>(null);

  // Sync state with admin context (po pierwszym refresh).
  useEffect(() => {
    if (admin) {
      setName(admin.name ?? '');
      setNewEmail(admin.email);
      setNewUsername(admin.username);
    }
  }, [admin]);

  if (!admin) {
    return (
      <Layout title={t('admin.profile.title')}>
        <div className="mx-auto max-w-3xl px-6 py-8">
          <p className="text-sm text-text-muted">{t('admin.profile.loading')}</p>
        </div>
      </Layout>
    );
  }

  async function handleSaveName(e: FormEvent) {
    e.preventDefault();
    if (savingName) return;
    setSavingName(true);
    const trimmed = name.trim();
    const newName = trimmed === '' ? null : trimmed;
    try {
      await api.patch('/admin/me', { name: newName });
      toast.show({
        variant: 'success',
        title: t('admin.profile.name.savedTitle'),
        description: t('admin.profile.name.savedDescription'),
      });
      await refresh();
    } catch {
      toast.show({
        variant: 'error',
        title: t('admin.profile.name.errors.unknownTitle'),
        description: t('admin.profile.name.errors.unknownDescription'),
      });
    }
    setSavingName(false);
  }

  function validateIdentifier(): boolean {
    if (!admin) return false;
    const errors: Record<string, string> = {};
    const e = newEmail.trim().toLowerCase();
    const u = newUsername.trim();

    if (!e) errors.email = t('admin.profile.identifier.errors.emailRequired');
    else if (!EMAIL_REGEX.test(e)) errors.email = t('admin.profile.identifier.errors.emailFormat');

    if (!u) errors.username = t('admin.profile.identifier.errors.usernameRequired');
    else if (!isValidUsername(u))
      errors.username = t('admin.profile.identifier.errors.usernameFormat');

    if (e === admin.email && u === admin.username) {
      errors.username = t('admin.profile.identifier.errors.noChange');
    }

    if (!identifierPassword) {
      errors.password = t('admin.profile.identifier.errors.passwordRequired');
    }

    setIdentifierFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSaveIdentifier(e: FormEvent) {
    e.preventDefault();
    if (savingIdentifier) return;
    if (!validateIdentifier()) return;

    setSavingIdentifier(true);
    setIdentifierApiError(null);

    const newE = newEmail.trim().toLowerCase();
    const newU = newUsername.trim();
    const payload: Record<string, string> = { currentPassword: identifierPassword };
    if (admin && newE !== admin.email) payload.email = newE;
    if (admin && newU !== admin.username) payload.username = newU;

    try {
      await api.post('/admin/me/change-identifier', payload);
      toast.show({
        variant: 'success',
        title: t('admin.profile.identifier.savedTitle'),
        description: t('admin.profile.identifier.savedDescription'),
      });
      // Sessions zostaly revoke'd po stronie BE — wylogujemy lokalnie i przeniesiemy do /login.
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      const axErr = err as AxiosError<BackendError>;
      const code = axErr.response?.data?.error;
      if (axErr.response?.status === 401 || code === 'BAD_PASSWORD') {
        setIdentifierFieldErrors((prev) => ({
          ...prev,
          password: t('admin.profile.identifier.errors.badPassword'),
        }));
      } else if (code === 'EMAIL_ALREADY_EXISTS') {
        setIdentifierFieldErrors((prev) => ({
          ...prev,
          email: t('admin.profile.identifier.errors.emailTaken'),
        }));
      } else if (code === 'USERNAME_ALREADY_EXISTS') {
        setIdentifierFieldErrors((prev) => ({
          ...prev,
          username: t('admin.profile.identifier.errors.usernameTaken'),
        }));
      } else {
        setIdentifierApiError(t('admin.profile.identifier.errors.unknown'));
      }
      setSavingIdentifier(false);
    }
  }

  async function handleChangePassword(e: FormEvent) {
    e.preventDefault();
    if (savingPassword) return;
    setPasswordPolicyErrors([]);
    setPasswordApiError(null);

    if (newPassword !== confirmPassword) {
      setPasswordApiError(t('admin.profile.password.errors.mismatch'));
      return;
    }

    setSavingPassword(true);
    try {
      await api.post('/admin/me/change-password', { currentPassword, newPassword });
      toast.show({
        variant: 'success',
        title: t('admin.profile.password.savedTitle'),
        description: t('admin.profile.password.savedDescription'),
      });
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      const axErr = err as AxiosError<BackendError>;
      const code = axErr.response?.data?.error;
      const status = axErr.response?.status;
      if (status === 401 || code === 'BAD_PASSWORD') {
        setPasswordApiError(t('admin.profile.password.errors.badPassword'));
      } else if (code === 'PASSWORD_POLICY_VIOLATION') {
        const details = Array.isArray(axErr.response?.data?.details)
          ? (axErr.response?.data?.details as string[])
          : [];
        setPasswordPolicyErrors(policyErrorsToMessages(details, t));
      } else if (code === 'PASSWORD_RECENTLY_USED') {
        setPasswordApiError(t('admin.profile.password.errors.recentlyUsed'));
      } else {
        setPasswordApiError(t('admin.profile.password.errors.unknown'));
      }
      setSavingPassword(false);
    }
  }

  return (
    <Layout title={t('admin.profile.title')}>
      <section className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-text-primary">{t('admin.profile.title')}</h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('admin.profile.subtitle')}</p>
        </div>

        {/* Read-only info */}
        <section
          className="mb-6 space-y-1 rounded-lg border border-border-secondary bg-bg-secondary p-4 text-xs text-text-secondary"
          data-testid="admin-profile-meta"
        >
          <div>
            <span className="font-semibold">{t('admin.profile.meta.role')}:</span>{' '}
            {t(`admin.adminUsers.role.${admin.role}`, { defaultValue: admin.role })}
          </div>
          <div>
            <span className="font-semibold">{t('admin.profile.meta.email')}:</span> {admin.email}
          </div>
          <div>
            <span className="font-semibold">{t('admin.profile.meta.username')}:</span>{' '}
            {admin.username}
          </div>
        </section>

        {/* Name */}
        <form
          onSubmit={handleSaveName}
          className="mb-8 space-y-3 rounded-lg border border-border-secondary bg-bg-elevated p-5"
          data-testid="admin-profile-name-form"
        >
          <h3 className="text-base font-semibold text-text-primary">
            {t('admin.profile.name.title')}
          </h3>
          <Input
            label={t('admin.profile.name.label')}
            type="text"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            disabled={savingName}
            data-testid="admin-profile-name-input"
          />
          <Button type="submit" loading={savingName} disabled={savingName}>
            {savingName ? t('admin.profile.name.saving') : t('admin.profile.name.save')}
          </Button>
        </form>

        {/* Identifier */}
        <form
          onSubmit={handleSaveIdentifier}
          className="mb-8 space-y-3 rounded-lg border border-border-secondary bg-bg-elevated p-5"
          data-testid="admin-profile-identifier-form"
        >
          <h3 className="text-base font-semibold text-text-primary">
            {t('admin.profile.identifier.title')}
          </h3>
          <p className="text-xs text-text-tertiary">{t('admin.profile.identifier.warning')}</p>
          <Input
            label={t('admin.profile.identifier.emailLabel')}
            type="email"
            value={newEmail}
            onChange={(ev) => setNewEmail(ev.target.value)}
            error={identifierFieldErrors.email}
            disabled={savingIdentifier}
            data-testid="admin-profile-identifier-email"
          />
          <Input
            label={t('admin.profile.identifier.usernameLabel')}
            type="text"
            value={newUsername}
            onChange={(ev) => setNewUsername(ev.target.value)}
            error={identifierFieldErrors.username}
            disabled={savingIdentifier}
            data-testid="admin-profile-identifier-username"
          />
          <Input
            label={t('admin.profile.identifier.passwordLabel')}
            type="password"
            autoComplete="current-password"
            value={identifierPassword}
            onChange={(ev) => setIdentifierPassword(ev.target.value)}
            error={identifierFieldErrors.password}
            disabled={savingIdentifier}
            data-testid="admin-profile-identifier-password"
          />
          {identifierApiError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="admin-profile-identifier-error"
            >
              {identifierApiError}
            </div>
          )}
          <Button type="submit" loading={savingIdentifier} disabled={savingIdentifier}>
            {savingIdentifier
              ? t('admin.profile.identifier.saving')
              : t('admin.profile.identifier.save')}
          </Button>
        </form>

        {/* Password */}
        <form
          onSubmit={handleChangePassword}
          className="mb-8 space-y-3 rounded-lg border border-border-secondary bg-bg-elevated p-5"
          data-testid="admin-profile-password-form"
        >
          <h3 className="text-base font-semibold text-text-primary">
            {t('admin.profile.password.title')}
          </h3>
          <p className="text-xs text-text-tertiary">{t('admin.profile.password.policyHint')}</p>
          <Input
            label={t('admin.profile.password.currentLabel')}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(ev) => setCurrentPassword(ev.target.value)}
            disabled={savingPassword}
            data-testid="admin-profile-password-current"
          />
          <Input
            label={t('admin.profile.password.newLabel')}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(ev) => setNewPassword(ev.target.value)}
            disabled={savingPassword}
            data-testid="admin-profile-password-new"
          />
          <Input
            label={t('admin.profile.password.confirmLabel')}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(ev) => setConfirmPassword(ev.target.value)}
            disabled={savingPassword}
            data-testid="admin-profile-password-confirm"
          />

          {passwordPolicyErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="admin-profile-password-policy-errors"
            >
              <ul className="list-inside list-disc space-y-1">
                {passwordPolicyErrors.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          {passwordApiError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="admin-profile-password-error"
            >
              {passwordApiError}
            </div>
          )}

          <Button type="submit" loading={savingPassword} disabled={savingPassword}>
            {savingPassword ? t('admin.profile.password.saving') : t('admin.profile.password.save')}
          </Button>
        </form>
      </section>
    </Layout>
  );
}
